/**
 * SCR-014 — Enrollments & class transfer (admin + manager).
 * Admin: branch filter + full access. Manager: scoped server-side.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@client/components/data-table';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import { useClasses } from '@client/lib/queries/use-classes';
import {
  type Enrollment,
  type EnrollmentStatus,
  enrollmentKeys,
  useEnrollments,
  useCreateEnrollment,
  useTransferEnrollment,
} from '@client/lib/queries/use-enrollments';
import { useStudents } from '@client/lib/queries/use-students';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/enrollments/')({
  component: EnrollmentsPage,
});

const STATUS_META: Record<EnrollmentStatus, { label: string; variant: 'success' | 'warning' | 'info' | 'destructive' | 'secondary' | 'outline' }> = {
  active:      { label: 'Đang học',     variant: 'success' },
  transferred: { label: 'Chuyển lớp',   variant: 'warning' },
  completed:   { label: 'Hoàn thành',   variant: 'info' },
  dropped:     { label: 'Đã nghỉ',      variant: 'destructive' },
};

// ─── New Enrollment Dialog ───────────────────────────────────────────────────

function NewEnrollmentDialog({
  open,
  onClose,
  branchId,
  role,
}: {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  role: string;
}) {
  const qc = useQueryClient();
  const createEnrollment = useCreateEnrollment();

  const [studentId, setStudentId] = useState('');
  const [classId, setClassId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overCapacity, setOverCapacity] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: studentsData } = useStudents({
    branchId: role === 'manager' ? undefined : branchId,
    status: 'active',
    pageSize: 100,
  });
  const { data: classesData } = useClasses({
    branchId: role === 'manager' ? undefined : branchId,
    pageSize: 100,
  });

  const students = studentsData?.items ?? [];
  const classes = (classesData?.items ?? []).filter(
    (c) => c.status !== 'cancelled' && c.status !== 'finished',
  );

  async function handleSave(confirm = false) {
    if (!studentId || !classId) { setError('Chọn đủ học viên và lớp.'); return; }
    setError(null);
    setSaving(true);
    try {
      await createEnrollment.mutateAsync({
        studentId,
        classId,
        note: note || undefined,
        confirmOverCapacity: confirm || undefined,
      });
      await qc.invalidateQueries({ queryKey: enrollmentKeys.all });
      toast.success('Đã ghi danh học viên.');
      onClose();
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setError('Học viên đã ghi danh lớp này.');
      } else if (isApiError(err) && err.status === 422) {
        const body = err.body as { warning?: string; currentEnrollment?: number; capacity?: number };
        if (body?.warning === 'over_capacity') {
          setOverCapacity(true);
          setError(`Lớp đã đủ sĩ số (${body.currentEnrollment}/${body.capacity}). Xác nhận để ghi danh vượt?`);
        } else {
          setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
        }
      } else {
        setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Ghi danh học viên</DialogTitle></DialogHeader>
        {error && (
          <Alert variant={overCapacity ? 'default' : 'destructive'}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Học viên <span className="text-destructive">*</span></Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger><SelectValue placeholder="Chọn học viên" /></SelectTrigger>
              <SelectContent>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.fullName} ({s.studentCode})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Lớp học <span className="text-destructive">*</span></Label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.code} — {c.name} ({c.currentEnrollment}/{c.capacity})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Ghi chú</Label>
            <Textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
            {overCapacity ? (
              <Button variant="destructive" onClick={() => void handleSave(true)} disabled={saving}>
                Xác nhận vượt sĩ số
              </Button>
            ) : (
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Đang lưu...' : 'Ghi danh'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transfer Dialog ─────────────────────────────────────────────────────────

function TransferDialog({
  enrollment,
  open,
  onClose,
  branchId,
  role,
}: {
  enrollment: Enrollment;
  open: boolean;
  onClose: () => void;
  branchId?: string;
  role: string;
}) {
  const qc = useQueryClient();
  const transferMutation = useTransferEnrollment(enrollment.id);

  const [toClassId, setToClassId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overCapacity, setOverCapacity] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: classesData } = useClasses({
    branchId: role === 'manager' ? undefined : branchId,
    pageSize: 100,
  });
  const classes = (classesData?.items ?? []).filter(
    (c) =>
      c.id !== enrollment.classId &&
      c.status !== 'cancelled' &&
      c.status !== 'finished',
  );

  async function handleSave(confirm = false) {
    if (!toClassId) { setError('Chọn lớp đích.'); return; }
    if (!effectiveDate) { setError('Chọn ngày hiệu lực.'); return; }
    setError(null);
    setSaving(true);
    try {
      await transferMutation.mutateAsync({
        toClassId,
        effectiveDate,
        reason: reason || undefined,
        confirmOverCapacity: confirm || undefined,
      });
      await qc.invalidateQueries({ queryKey: enrollmentKeys.all });
      toast.success('Đã chuyển lớp thành công.');
      onClose();
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const body = err.body as { warning?: string; currentEnrollment?: number; capacity?: number; message?: string };
        if (body?.warning === 'over_capacity') {
          setOverCapacity(true);
          setError(`Lớp đích đã đủ sĩ số (${body.currentEnrollment}/${body.capacity}). Xác nhận chuyển?`);
        } else {
          setError(body?.message ?? 'Không thể chuyển lớp.');
        }
      } else {
        setError(isApiError(err) ? err.message : 'Lỗi máy chủ.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>Chuyển lớp — {enrollment.studentName}</DialogTitle>
        </DialogHeader>
        {error && (
          <Alert variant={overCapacity ? 'default' : 'destructive'}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
            Lớp hiện tại: <span className="font-mono font-medium text-foreground">{enrollment.classCode ?? enrollment.classId}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Lớp đích <span className="text-destructive">*</span></Label>
            <Select value={toClassId} onValueChange={setToClassId}>
              <SelectTrigger><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.code} — {c.name} ({c.currentEnrollment}/{c.capacity})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Ngày hiệu lực <span className="text-destructive">*</span></Label>
            <Input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Lý do</Label>
            <Textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
            {overCapacity ? (
              <Button variant="destructive" onClick={() => void handleSave(true)} disabled={saving}>
                Xác nhận chuyển
              </Button>
            ) : (
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Đang chuyển...' : 'Chuyển lớp'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

function EnrollmentsPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();
  const { selectedBranchId } = useBranchFilter();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState<Enrollment | null>(null);

  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    branchId: effectiveBranch,
    classId: classFilter !== 'all' ? classFilter : undefined,
    status: statusFilter !== 'all' ? (statusFilter as EnrollmentStatus) : undefined,
  };

  const { data, isLoading, isError, refetch } = useEnrollments(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const { data: classesData } = useClasses({ branchId: effectiveBranch, pageSize: 100 });
  const classes = classesData?.items ?? [];

  async function handleMarkStatus(
    enrollment: Enrollment,
    status: 'completed' | 'dropped',
  ) {
    const label = status === 'completed' ? 'hoàn thành' : 'nghỉ';
    if (!confirm(`Đánh dấu học viên "${enrollment.studentName}" ${label}?`)) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.patch(`/api/enrollments/${enrollment.id}`, {
        status,
        ...(status === 'completed' ? { completedAt: today } : { droppedAt: today }),
      });
      await qc.invalidateQueries({ queryKey: enrollmentKeys.all });
      toast.success(status === 'completed' ? 'Đã đánh dấu hoàn thành.' : 'Đã đánh dấu nghỉ.');
    } catch (err) {
      toast.error(isApiError(err) ? err.message : 'Không thể cập nhật.');
    }
  }

  const columns: ColumnDef<Enrollment>[] = [
    {
      accessorKey: 'studentName',
      header: 'Học viên',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <Link
            to="/admin/students/$studentId"
            params={{ studentId: row.original.studentId }}
            className="font-medium text-sm hover:underline text-primary"
          >
            {row.original.studentName ?? row.original.studentId}
          </Link>
          <span className="text-xs text-muted-foreground font-mono">
            {row.original.studentCode ?? ''}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'classCode',
      header: 'Lớp',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <Link
            to="/admin/classes/$classId"
            params={{ classId: row.original.classId }}
            className="font-mono text-sm text-primary hover:underline"
          >
            {row.original.classCode ?? row.original.classId}
          </Link>
          <span className="text-xs text-muted-foreground">
            {row.original.className ?? ''}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => row.original.branchName ?? '—',
    },
    {
      accessorKey: 'enrolledAt',
      header: 'Ngày ghi danh',
      cell: ({ row }) =>
        row.original.enrolledAt
          ? format(new Date(row.original.enrolledAt), 'dd/MM/yyyy')
          : '—',
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => {
        const meta = STATUS_META[row.original.status] ?? STATUS_META['active'];
        return (
          <Badge variant={meta?.variant} className="text-xs">
            {meta?.label}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const e = row.original;
        const canMutate = e.status === 'active';
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canMutate && (
                <>
                  <DropdownMenuItem onClick={() => setTransferTarget(e)}>
                    Chuyển lớp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => void handleMarkStatus(e, 'completed')}>
                    Đánh dấu hoàn thành
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => void handleMarkStatus(e, 'dropped')}
                  >
                    Đánh dấu nghỉ
                  </DropdownMenuItem>
                </>
              )}
              {!canMutate && (
                <DropdownMenuItem disabled className="text-muted-foreground text-xs">
                  Không có hành động
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Ghi danh & chuyển lớp</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={() => setEnrollDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Ghi danh
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có ghi danh nào."
        searchPlaceholder="Tìm học viên/lớp..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {role === 'admin' && (
              <Select
                value={branchFilter}
                onValueChange={(v) => { setBranchFilter(v); setPage(1); }}
              >
                <SelectTrigger className="h-10 w-[160px]">
                  <SelectValue placeholder="Cơ sở" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả cơ sở</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={classFilter}
              onValueChange={(v) => { setClassFilter(v); setPage(1); }}
            >
              <SelectTrigger className="h-10 w-[160px]">
                <SelectValue placeholder="Lớp" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả lớp</SelectItem>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <SelectTrigger className="h-10 w-[140px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* New enrollment dialog */}
      <NewEnrollmentDialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        branchId={effectiveBranch}
        role={role}
      />

      {/* Transfer dialog */}
      {transferTarget && (
        <TransferDialog
          enrollment={transferTarget}
          open={!!transferTarget}
          onClose={() => setTransferTarget(null)}
          branchId={effectiveBranch}
          role={role}
        />
      )}
    </div>
  );
}
